import emojiRegex from 'emoji-regex';

import type { WS_MSG_MULTIPLE_TEMPLATE_PARAMS_CITATION_NOTE } from '@/components/chat-app/interface/chat-content';

import {
  ChatListCitationElementContainerClassName,
  ChatListCitationNoteElementContainerClassName
} from '../../useCitationPopover';
import styles from './index.less';

export interface MentionMember {
  id: string;
  username: string;
}

/**
 * 将消息中的 @username 转换为 <at user_id="id">username</at> 格式
 * @param content 原始消息内容
 * @param members 群聊成员列表，包含 id 和 username
 * @returns 转换后的消息内容
 *
 * @example
 * // 输入: "哈哈@copilot 你好呀", members: [{ id: "123", username: "copilot" }]
 * // 输出: "哈哈<at user_id="123">copilot</at> 你好呀"
 */
export function transformMentions(content: string, members: MentionMember[]): string {
  if (!content || members.length === 0) {
    return content;
  }

  let result = content;

  // 按 username 长度降序排序，避免短名字匹配到长名字的一部分
  const sortedMembers = [...members].sort((a, b) => b.username.length - a.username.length);

  sortedMembers.forEach((member) => {
    // 使用全局正则匹配所有 @username
    const regex = new RegExp(`@${escapeRegExp(member.username)}(?=\\s|$|[^\\w])`, 'g');

    result = result.replace(regex, `<at user_id="${member.id}">${member.username}</at>`);
  });

  return result;
}

/**
 * 将消息中的 <at user_id="id">username</at> 转换回 @username 格式
 * @param content 包含 <at> 标签的消息内容
 * @returns 转换后的消息内容
 *
 * @example
 * // 输入: "哈哈<at user_id="123">copilot</at> 你好呀"
 * // 输出: "哈哈@copilot 你好呀"
 */
export function parseMentions(content: string, t: (key: string) => string): string {
  if (!content) {
    return content;
  }

  return content.replace(
    /<at\s+user_id="([^"]*)">([^<]*)<\/at>/g,
    (_match, userId: string, username: string) => {
      if (userId === 'all') {
        return `@${t('chatApp.input.mentionAll')}`;
      }

      return `@${username}`;
    }
  );
}

/**
 * 转义正则表达式中的特殊字符
 */
function escapeRegExp(str: string): string {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function replaceExtractList(originStr: string, targetStr: string, list: string[]): string {
  let result = originStr;

  list.forEach((item) => {
    result = result.replace(item, targetStr);
  });

  return result;
}

export function getParentContainer(elemFrom: Element, parentId: string): Element | null {
  let curEle: Element | null = elemFrom;

  while (curEle) {
    if (curEle === document.body) {
      return null;
    }

    if (curEle.id === parentId) {
      return curEle;
    }

    curEle = curEle.parentElement;
  }

  return null;
}

export function insertCitation(_content: string): string {
  let result = '';
  // 抽取 原点数据
  // TODO: 改造成字符串，因为不需要是数组了；
  const matches = [_content];

  if (matches.length) {
    const allOK = matches.every(
      (item) => item.includes('citationId') && (item.includes('link') || item.includes('noteId'))
    );

    if (allOK) {
      const replacedMessage = replaceExtractList(_content, '---CITATION_PLACEHOLDER---', matches);
      let matchesIndex = 0;
      const arr = replacedMessage.split(/(---CITATION_PLACEHOLDER---)/);
      const linkArr: string[] = [];

      let lastCitationLink = '';

      arr.forEach((arrItem: string) => {
        if (arrItem) {
          if (arrItem === '---CITATION_PLACEHOLDER---') {
            const jsonStr = matches[matchesIndex];

            // TODO 丢失了开头引用的问题
            if (jsonStr) {
              try {
                const tryObj = JSON.parse(jsonStr);

                // 普通的citation
                if (tryObj['link']) {
                  const jsonObj = JSON.parse(jsonStr) as {
                    link: string;
                    index: number;
                    summary?: string;
                    title?: string;
                  };

                  // 连续相同的URL,合并,即不展示
                  if (jsonObj.link !== lastCitationLink) {
                    lastCitationLink = jsonObj.link;
                    // 之后的 citation index 都是 @jinkai 直出，这里是为了兼容老逻辑
                    let citationIndex = jsonObj.index;
                    const linkInfo = {
                      link: jsonObj.link,
                      title: jsonObj.title || '',
                      summary: jsonObj.summary || ''
                    };

                    if (!citationIndex) {
                      // 消息内的规则:1 1 2 2 3 4 5 1
                      // 相同的url不过滤,客户端的引用序号为第一次出现的编号
                      citationIndex = linkArr.length + 1;

                      if (linkArr.includes(jsonObj.link)) {
                        citationIndex = linkArr.indexOf(jsonObj.link) + 1;
                      } else {
                        linkArr.push(jsonObj.link);
                      }
                    }

                    if (jsonObj.link) {
                      // eslint-disable-next-line no-useless-escape
                      result += `<span class="${ChatListCitationElementContainerClassName} ${
                        styles.citationAnchorLink
                      }" data-link="${jsonObj.link}" data-link-info="${encodeURIComponent(
                        JSON.stringify(linkInfo)
                      )}"><span>${citationIndex}</span></span>`;
                    }
                  }
                }
                // CitationNote
                else {
                  const jsonObj = JSON.parse(
                    jsonStr
                  ) as WS_MSG_MULTIPLE_TEMPLATE_PARAMS_CITATION_NOTE;

                  if (jsonObj.noteId) {
                    // eslint-disable-next-line no-useless-escape
                    result += `<span class="${ChatListCitationNoteElementContainerClassName} ${
                      styles.citationNoteAnchorLink
                    }" data-detail="${encodeURIComponent(jsonStr)}"><span>${
                      jsonObj.index
                    }</span></span>`;
                  }
                }

                matchesIndex++;
              } catch (e) {
                console.log(e);
              }
            }
          } else {
            lastCitationLink = '';
            result += arrItem;
          }
        }
      });
    }
  }

  if (!result) {
    return _content;
  }

  return result;
}

export function rendererText(text: string): string {
  // 使用 emoji-regex 识别文本中的 emoji
  const regex = emojiRegex();

  // 处理文本，将每个单词（以空格分隔）包装在 fade-in-text 标签中
  const words = text.split(' ');
  const processedWords = words.map((word, index, self) => {
    if (!word) {
      if (index === self.length - 1) {
        return '';
      }

      return ' ';
    }

    // 检查单词中是否包含 emoji
    let result = '';
    let lastIndex = 0;
    let match;

    // 创建新的正则表达式实例进行匹配
    const wordRegex = new RegExp(regex);

    // 处理单词中的每个 emoji
    while ((match = wordRegex.exec(word)) !== null) {
      // 添加 emoji 前的文本
      result += word.substring(lastIndex, match.index);

      // 将 emoji 包裹在 span.emoji 标签中
      result += `<span class="emoji">${match[0]}</span>`;

      // 更新下一次匹配的起始位置
      lastIndex = match.index + match[0].length;
    }

    // 添加最后一个 emoji 后的文本
    result += word.substring(lastIndex);

    // 将处理后的单词包装在 fade-in-text 标签中
    if (index === self.length - 1) {
      return `<fade-in-text>${result}</fade-in-text>`;
    }

    return `<fade-in-text>${result}</fade-in-text> `;
  });

  return processedWords.join('');
}

export function rendererLink(
  href: string | null,
  title: string | null | undefined,
  text: string
): string | false {
  return `<a target="_blank" href="${href || ''}"${title ? ' title="' + title + '"' : ''}>${
    text || ''
  }</a>`;
}

export function rendererListItem(text: string): string {
  return `<li>${text.replace(/<p>/g, '').replace(/<\/p>/g, '')}</li>\n`; // 简单粗暴，但可能会有未知风险
  // if (/^<p>(.+)<\/p>\n$/.test(text)) {
  //   return `<li>${RegExp.$1}</li>\n`;
  // }

  // if (/^<p>(.+)<\/p>\n<ul>((:?.|\n)+)<\/ul>\n$/.test(text)) {
  //   return `<li>${RegExp.$1}<ul>${RegExp.$2}</ul></li>\n`;
  // }

  // return `<li>${text}</li>\n`;
}
